import React from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'
import { DEFAULT_FONT_STYLES } from 'css/theme'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { selectContrastingColor } from '@classy/campaign-page-blocks'

interface DonorInfoSummaryCardProps {
  className?: string
}

const CardContainer = styled.div<{ backgroundColor: string }>`
  background: ${({ backgroundColor }) => backgroundColor};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`

const DonorName = styled.div<{ textColor: string }>`
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--body-default__font-size']};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--body-bold__font-weight']};
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--body-default__line-height']};
  color: ${({ textColor }) => textColor};
  margin-bottom: 4px;
`

const DonorEmail = styled.div<{ textColor: string }>`
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--body-sm__font-size']};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--body-default__font-weight']};
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--body-sm__line-height']};
  color: ${({ textColor }) => textColor};
  opacity: 0.8;
`

const DonorInfoSummaryCard: React.FC<DonorInfoSummaryCardProps> = ({ className }) => {
  const { t } = useTranslation()
  const order = useOrder()
  const config = useConfig()

  // Get campaign theme colors
  const secondaryColor = config.theme['--theme-color__brand-secondary']

  // Calculate contrasting text color for the secondary background
  const textColor = selectContrastingColor(secondaryColor)

  const { firstName, lastName, email } = order.contact.value

  if (!firstName && !lastName && !email) {
    return null
  }

  const donorName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    t('DonorInfoSummaryCard.anonymousDonor', 'Anonymous Donor')

  const emailDisplay = email || t('DonorInfoSummaryCard.noEmail', 'No email provided')

  return (
    <CardContainer className={className} backgroundColor={secondaryColor}>
      <DonorName textColor={textColor}>{donorName}</DonorName>
      <DonorEmail textColor={textColor}>
        {t('DonorInfoSummaryCard.emailLabel', 'Email')}: {emailDisplay}
      </DonorEmail>
    </CardContainer>
  )
}

export default DonorInfoSummaryCard
